import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Space,
  Upload,
  Form,
  message,
  Drawer,
  Table,
  Dropdown,
  Modal,
} from "antd";
import {
  PlusCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  MoreOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import excelIcon from "../../../excel-icon.svg";
import pdfIcon from "../../../pdf-icon.svg";
import documentIcon from "../../../document-icon.svg";
import imageIcon from "../../../image-icon.svg";
import { useParams } from "react-router-dom";
import AttachmentComponent from "CommonComponents/Attachments/Attachments";
import { useForm } from "antd/es/form/Form";
import FormType from "features/Enums/FormType";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  deleteAttachment,
  getAttachments,
  uploadAttachment,
} from "CommonComponents/Attachments/AttachmentsAPI";
import {
  markFileForDeletion,
  removeExistingFile,
} from "CommonComponents/Attachments/AttachmentsSlice";
import { FormattedMessage, useIntl } from "react-intl";
import OpenNotificationWithIcon from "CommonComponents/ResultNotification/Notification";
function FilesTab() {
  const { Dragger } = Upload;
  const { Column } = Table;
  const intl = useIntl();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { leadId } = useParams();
  const attachments = useSelector((state) => state.Attachments.attachments);
  const [fileList, setFileList] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const formTypeId = Object.values(FormType).find(
    (f) => f.name === "LeadFiles"
  )?.id;
  useEffect(() => {
    dispatch(getAttachments(leadId, formTypeId));
  }, [dispatch, leadId, formTypeId]);
  const [open, setOpen] = useState(false);
  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };
  useEffect(() => {
    const docs =
      attachments?.[formTypeId]?.[leadId]?.documents?.filter(
        (doc) => !doc.isThumbnail
      ) || [];

    const mapped = docs.map((doc, index) => {
      const originalFileName = doc.originalFileName || "";
      const onlyFileName = originalFileName.replace(/\.[^/.]+$/, "");
      const kind =
        doc.originalFileName?.split(".").pop()?.toLowerCase() || "doc";
      return {
        key: doc.guidFileName || `file-${index}`,
        uid: doc.guidFileName,
        id: doc.id,
        Sno: `${index + 1}`.padStart(2, "0"),
        filename: onlyFileName,
        url: `data:application/octet-stream;base64,${doc.base64Content}`,
        date: doc.createdDate,
        kind: kind,
        size: "15kb",
        base64: doc.base64Content,
      };
    });

    setDisplayData(mapped);
  }, [attachments]);
  const onSave = () => {
    if (leadId) {
      dispatch(uploadAttachment(leadId, formTypeId)).then(() => {
        setOpen(false);
        dispatch(getAttachments(leadId, formTypeId));
      });
    }
  };

  const onRemove = async (file) => {
    Modal.confirm({
      title: "Are you sure to delete this file?",
      okText: intl.formatMessage({ id: "Common.Yes" }),
      okType: "danger",
      cancelText: intl.formatMessage({ id: "Common.No" }),
      async onOk() {
        try {
          //setFileList(fileList.filter((f) => f.uid !== file.uid));
          setDisplayData((prev) => prev.filter((f) => f.uid !== file.uid));
          dispatch(
            markFileForDeletion({
              identifier: formTypeId,
              id: leadId,
              fileId: file.id,
            })
          );
          dispatch(
            removeExistingFile({
              identifier: formTypeId,
              id: leadId,
              fileId: file.id,
            })
          );
          await dispatch(deleteAttachment([file.id]));
          dispatch(getAttachments(leadId, formTypeId));
        } catch (error) {
          setFileList([...fileList]);
          OpenNotificationWithIcon(
            "error",
            "Common.Error",
            "",
            "LeadFile.Error.FailedtoDelete"
          );
        }
      },
    });
  };

  const handlePreview = (file) => {
    try {
      if (file.kind === "pdf") {
        const pdfWindow = window.open();
        pdfWindow.document.write(
          `<iframe width='100%' height='100%' src='data:application/pdf;base64,${file.base64}'></iframe>`
        );
      } else if (["jpg", "jpeg", "png", "gif"].includes(file.kind)) {
        const imageWindow = window.open();
        imageWindow.document.write(
          `<img src='data:image/${file.kind};base64,${file.base64}' style='max-width:100%; max-height:100%'/>`
        );
      } else {
        handleDownload(file);
      }
    } catch (error) {
      console.error("Preview error:", error);
      OpenNotificationWithIcon(
        "error",
        "Common.Error",
        "",
        "LeadFile.Error.FailedtoPreview"
      );
    }
  };

  const handleDownload = (file) => {
    try {
      const blob = base64ToBlob(file.base64, getMimeType(file.kind));
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${file.filename}.${file.kind}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      OpenNotificationWithIcon(
        "error",
        "Common.Error",
        "",
        "LeadFile.Error.FailedtoDownload"
      );
    }
  };

  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const getMimeType = (fileExtension) => {
    const mimeTypes = {
      pdf: "application/pdf",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      txt: "text/plain",
    };
    return mimeTypes[fileExtension.toLowerCase()] || "application/octet-stream";
  };

  const getDropdownItems = (file) => [
    {
      label: (
        <a onClick={() => handlePreview(file)}>
          <EyeOutlined />
          &nbsp;&nbsp;
          <FormattedMessage id="Common.Preview" />
        </a>
      ),
      key: "0",
    },
    {
      label: (
        <a onClick={() => handleDownload(file)}>
          <DownloadOutlined />
          &nbsp;&nbsp;
          <FormattedMessage id="Common.Download" />
        </a>
      ),
      key: "1",
    },
    {
      label: (
        <a onClick={() => onRemove(file)}>
          <DeleteOutlined />
          &nbsp;&nbsp;
          <FormattedMessage id="Common.Delete" />
        </a>
      ),
      key: "2",
    },
  ];
  // const formatFileSize = (bytes) => {
  //   if (bytes === 0) return "0 Bytes";
  //   const k = 1024;
  //   const sizes = ["Bytes", "KB", "MB", "GB"];
  //   const i = Math.floor(Math.log(bytes) / Math.log(k));
  //   return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  // };
  const getFileIcon = (kind) => {
    switch (kind) {
      case "xls":
      case "xlsx":
        return <img src={excelIcon} className="iconsize" />;
      case "pdf":
        return <img src={pdfIcon} className="iconsize" />;
      case "jpg":
      case "png":
      case "jpeg":
        return <img src={imageIcon} className="iconsize" />;
      default:
        return <img src={documentIcon} className="iconsize" />;
    }
  };
  return (
    <>
      <div size="small" className="notes-btn">
        <Button
          color="primary"
          variant="dashed"
          block
          onClick={showDrawer}
          className="mb-10 new-btn-add"
        >
          <PlusCircleOutlined />
          {<FormattedMessage id="LeadFile.AddnewFile" />}
        </Button>
        <Table
          dataSource={displayData}
          bordered
          pagination={false}
          scroll={{
            x: 600,
          }}
        >
          <Column title="S.No" dataIndex="Sno" key="Sno" width={60} />
          <Column
            title={<FormattedMessage id="LeadFile.FileName" />}
            dataIndex="filename"
            key="filename"
            render={(text, record) => (
              <>
                <Space>
                  {getFileIcon(record.kind)}
                  {text}
                </Space>
              </>
            )}
          />
          <Column
            title={<FormattedMessage id="LeadFile.Date" />}
            dataIndex="date"
            key="date"
            render={(date) =>
              date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "--"
            }
          />
          <Column
            title={<FormattedMessage id="LeadFile.Kind" />}
            dataIndex="kind"
            key="kind"
          />
          {/* <Column
            title="Size"
            dataIndex="size"
            key="size"
            render={(size) => formatFileSize(Number(size))}
          /> */}
          <Column
            title={<FormattedMessage id="LeadFile.Actions" />}
            width={70}
            key="action"
            render={(_, record) => (
              <Dropdown
                menu={{ items: getDropdownItems(record) }}
                trigger={["click"]}
              >
                <a onClick={(e) => e.preventDefault()}>
                  <Space>
                    <MoreOutlined />
                  </Space>
                </a>
              </Dropdown>
            )}
          />
        </Table>
        <Drawer
          title={<FormattedMessage id="LeadFile.AddnewFile" />}
          onClose={onClose}
          open={open}
          width="500px"
        >
          <Form
            name="file-form"
            layout="vertical"
            autoComplete="off"
            size="large"
            form={form}
            onFinish={onSave}
          >
            <Form.Item
              label={<FormattedMessage id="Common.Attachment" />}
              name="attachment"
              // rules={[
              //   {
              //     required: true,
              //   },
              // ]}
            >
              <AttachmentComponent
                identifier={formTypeId}
                currentId={leadId}
                getAttachments={true}
              />
            </Form.Item>
            <Row>
              <Col span={24} className="text-center mt-10">
                <Space>
                  <Button
                    danger
                    //variant="outlined"
                    size="medium"
                    onClick={onClose}
                  >
                    <CloseCircleOutlined />{" "}
                    {<FormattedMessage id="Common.Cancel" />}
                  </Button>
                  <Button type="primary" size="medium" htmlType="submit">
                    <CheckCircleOutlined />{" "}
                    {<FormattedMessage id="Common.Save" />}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Drawer>
      </div>
    </>
  );
}

export default FilesTab;
